import type { Manifest, Part, Sentence, Word } from "../api";

/**
 * The player works in one continuous chapter timeline, while the server stores
 * timings per part. This module is the arithmetic between the two, kept out of
 * the component so it can be tested without a DOM or an audio element.
 */

/** partAt returns the part containing a chapter-relative position. */
export function partAt(manifest: Manifest, positionMs: number): Part | undefined {
  if (manifest.parts.length === 0) return undefined;
  let found = manifest.parts[0]!;
  for (const part of manifest.parts) {
    if (part.start_ms <= positionMs) found = part;
    else break;
  }
  return found;
}

/**
 * playingPart is the part whose audio the element has loaded, by the index the
 * player stamps on it.
 *
 * Not partAt(position). At the very end of a part the element's time can run a
 * few milliseconds past the part's recorded duration, which puts the position
 * on the next part's start before `ended` fires; a player that took its part
 * from the position then moved on from the wrong one and skipped a whole part
 * (the owner's "Soniox Mid Chunk": 1:25 jumped to the part after next).
 */
export function playingPart(manifest: Manifest, partIndex: string | undefined): Part | undefined {
  if (partIndex === undefined || partIndex === "") return undefined;
  return manifest.parts.find((p) => String(p.index) === partIndex);
}

/**
 * afterPart is what to play when a part finishes, and whether the chapter is
 * over (ADR-0063).
 *
 * **By position in the list, never by index + 1.** A failed part is left out of
 * the manifest, so on a partial render the indexes are not contiguous — the
 * owner's English chapter ran 0…6, 8, 9, … and a player asking for index 7
 * found nothing, stopped dead 1:37 into 21 minutes, and recorded the chapter
 * as finished. A part's index is its identity in the render, which is what the
 * studio retries and what the source offsets belong to; it never says what
 * comes next.
 *
 * `end` is true only when the part that finished is the **last one in the
 * list** — the end of the audio. Running out of parts to play is a fact about
 * the list, and completion is sticky on the server, so a hole must never be
 * read as an ending. A part the manifest does not hold is neither: nothing to
 * play, and nothing finished.
 */
export function afterPart(
  manifest: Manifest,
  finished: Part | undefined,
): {
  next?: Part;
  end: boolean;
} {
  if (!finished) return { end: false };
  const at = manifest.parts.findIndex((p) => p.index === finished.index);
  if (at < 0) return { end: false };
  const next = manifest.parts[at + 1];
  return { next, end: !next };
}

/**
 * positionInPart is the chapter position for a time inside the part being
 * played, kept inside that part: past its recorded end it stays on its last
 * millisecond until the element says the part has ended.
 */
export function positionInPart(part: Part, withinMs: number): number {
  const within = Math.max(0, withinMs);
  const last = part.duration_ms > 0 ? part.duration_ms - 1 : within;
  return part.start_ms + Math.min(within, last);
}

/** offsetWithinPart converts a chapter position into a position inside a part. */
export function offsetWithinPart(part: Part, positionMs: number): number {
  return Math.max(0, positionMs - part.start_ms);
}

/**
 * allSentences flattens the manifest into one ordered list.
 *
 * A part whose alignment failed is still played (its verdict was given when it
 * was recorded) but has no sentences, and a server before this fix sent them as
 * null. A null in this list took the whole reader down (Apology, 2026-10-22);
 * such a part only leaves a stretch with nothing highlighted.
 */
export function allSentences(manifest: Manifest): Sentence[] {
  return manifest.parts.flatMap((p) => p.sentences ?? []);
}

/**
 * sentenceAt finds the sentence being spoken at a position.
 *
 * Binary search rather than a scan: this runs on every timeupdate, which fires
 * about four times a second, and a chapter can hold thousands of sentences.
 */
export function sentenceAt(sentences: Sentence[], positionMs: number): number {
  let lo = 0;
  let hi = sentences.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = sentences[mid]!;
    if (positionMs < s.start_ms) {
      hi = mid - 1;
    } else if (positionMs >= s.end_ms) {
      found = mid; // past this one; remember it in case nothing later matches
      lo = mid + 1;
    } else {
      return mid;
    }
  }
  // Between sentences — in a pause. The one just finished stays highlighted,
  // which reads as continuous rather than flickering off between sentences.
  return found;
}

/** wordAt finds the word being spoken within a sentence. */
export function wordAt(sentence: Sentence, positionMs: number): number {
  for (let i = sentence.words.length - 1; i >= 0; i--) {
    if (positionMs >= sentence.words[i]!.start_ms) return i;
  }
  return -1;
}

/** fractionOf is the progress-bar value for a position. */
export function fractionOf(manifest: Manifest, positionMs: number): number {
  if (manifest.duration_ms <= 0) return 0;
  return Math.min(1, Math.max(0, positionMs / manifest.duration_ms));
}

/**
 * isComplete decides when a chapter counts as finished.
 *
 * Not "reached the end": listeners stop during the last few seconds of trailing
 * speech, and a chapter that never marks itself finished is worse than one that
 * marks itself slightly early. Completion is sticky on the server, so a false
 * positive is permanent — 98% is the compromise.
 */
export function isComplete(manifest: Manifest, positionMs: number): boolean {
  return fractionOf(manifest, positionMs) >= COMPLETE_AT;
}

/** How far through a chapter counts as its end, for audio and for text. */
export const COMPLETE_AT = 0.98;

/**
 * textResume is where a chapter without audio reopens (ADR-0058): its place,
 * where the reader last was, or the top (null) when nothing is saved or the
 * place is at the end. Neither `completed` nor the fraction decides it: a
 * chapter scrolled to its end once is completed for good and its fraction
 * stays at the end, and it reopened ahead of where the reader had left it
 * (owner, 2026-10-04). The fraction stands in for a place a server never sent.
 */
export function textResume(saved: { fraction: number; place?: number }): number | null {
  const place = saved.place ?? saved.fraction;
  return place > 0 && place < COMPLETE_AT ? place : null;
}

/** wordKey identifies a word for React, stable across re-renders. */
export function wordKey(word: Word): string {
  return `${word.offset}-${word.length}`;
}

/**
 * clock formats a position for the transport readout.
 *
 * Hours only appear when there are hours: a 6-minute chapter reading "0:06:12"
 * makes the reader parse a field that is always zero.
 */
export function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const mm = hours > 0 ? String(minutes).padStart(2, "0") : String(minutes);
  return `${hours > 0 ? `${hours}:` : ""}${mm}:${String(seconds).padStart(2, "0")}`;
}

/**
 * sentenceForCitation finds where a cited passage starts in the transcript.
 *
 * Matched by containment, not by offset. A citation's offset is into the
 * revision body; the transcript is the alignment manifest, which indexes the
 * effective text of the render being played — one saved fix separates the two,
 * so seeking to a citation's offset would land in the wrong place by however
 * much the fixes before it shifted the text.
 *
 * A passage is several sentences of contiguous text, so every transcript
 * sentence it contains is contiguous too, and the first one in document order
 * is where the passage begins. Returns -1 when the cited text is not in this
 * render at all, which is the honest answer when the chapter has been
 * re-narrated since.
 */
export function sentenceForCitation(sentences: Sentence[], citation: string): number {
  if (citation === "") return -1;
  for (let i = 0; i < sentences.length; i++) {
    const text = sentences[i]?.text.trim() ?? "";
    // Short fragments match by accident — a lone "او." is inside half the
    // passages in a book — and landing confidently in the wrong paragraph is
    // worse than not moving.
    if (text.length < 12) continue;
    if (citation.includes(text)) return i;
  }
  return -1;
}
